import type { ParsedUrlQuery } from 'querystring';

/**
 * Restores query params that a middleware rewrite appended to the request URL.
 *
 * When the proxy rewrites a request (e.g. `/toronto-on-real-estate/houses` →
 * `/toronto-on-real-estate/filter?townhouse=false&has-image=false` for users who opted out of
 * imageless listings), Next.js exposes the rewritten pathname in `resolvedUrl` but drops the
 * rewritten query string from it — the params only surface in `context.query`. Address parsing
 * reads the URL string, so without this the filters are silently lost and pages fall back to
 * the unfiltered defaults.
 *
 * URLs whose `resolvedUrl` already carries a query string (direct navigation) are returned as-is.
 *
 * @param resolvedUrl The `context.resolvedUrl` from getServerSideProps
 * @param query The `context.query` from getServerSideProps
 * @param routeParamKeys Dynamic route param keys to exclude (e.g. `areaPath` for `[...areaPath]`)
 */
export function restoreRewrittenQueryParams(resolvedUrl: string, query: ParsedUrlQuery, routeParamKeys: readonly string[] = ['areaPath']): string {
  if (resolvedUrl.includes('?')) {
    return resolvedUrl;
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (routeParamKeys.includes(key) || value === undefined) {
      continue;
    }
    for (const singleValue of Array.isArray(value) ? value : [value]) {
      params.append(key, singleValue);
    }
  }

  const queryString = params.toString();
  return queryString ? `${resolvedUrl}?${queryString}` : resolvedUrl;
}
